import PropTypes from 'prop-types';

// @mui
import {styled, alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';

// icons
import Iconify from 'packages/common/components/iconify';

// constants
import {SERVICE_TYPES} from '../constants/reservation';

/**
 * @file Bouton bascule de sélection de service (midi/soir) avec icône et thème dédiés.
 * @module packages.common.components.table-reservation.components.ServiceToggleButton
 */

// ----------------------------------------------------------------------

/**
 * Enhanced toggle button for service selection with integrated icon and styling
 */
const ServiceToggleButton = styled(
    ({serviceType, children, ...other}) => {
      // Determine icon and colors based on serviceType
      const isLunch = serviceType === SERVICE_TYPES.LUNCH;
      const icon = isLunch ? 'mdi:white-balance-sunny' : 'mdi:weather-night';
      const colorKey = isLunch ? 'warning' : 'primary';

      return (
        <ToggleButton {...other}>
          <Box sx={{textAlign: 'center'}}>
            <Box sx={{display: 'flex', justifyContent: 'center', mb: 1}}>
              <Iconify
                icon={icon}
                width={32}
                height={32}
                sx={{
                  'color': `${colorKey}.main`,
                  'transition': (theme) => theme.transitions.create(['transform', 'color'], {
                    duration: theme.transitions.duration.shorter,
                  }),
                  '.Mui-selected &': {
                    transform: 'scale(1.2)',
                  },
                }}
              />
            </Box>
            {children}
          </Box>
        </ToggleButton>
      );
    },
)(({theme, serviceType}) => {
  // Determine colors based on serviceType
  const isLunch = serviceType === SERVICE_TYPES.LUNCH;
  const mainColor = isLunch ? theme.palette.warning : theme.palette.primary;
  const hoverColor = isLunch ? theme.palette.warning.light : theme.palette.primary.light;

  return {
    'flex': 1,
    'padding': theme.spacing(2),
    'borderRadius': theme.shape.borderRadius,
    'transition': theme.transitions.create(['all'], {
      duration: theme.transitions.duration.standard,
    }),
    '&.Mui-selected': {
      'backgroundColor': alpha(mainColor.main, 0.1),
      'color': mainColor.main,
      'borderColor': mainColor.main,
      'transform': 'translateY(-2px)',
      'boxShadow': `0 4px 8px ${alpha(mainColor.main, 0.25)}`,
      '&:hover': {
        backgroundColor: alpha(mainColor.main, 0.2),
      },
    },
    '&:hover': {
      backgroundColor: alpha(hoverColor, 0.15),
      transform: 'translateY(-1px)',
      boxShadow: `0 2px 4px ${alpha(theme.palette.text.primary, 0.15)}`,
    },
    '&.Mui-disabled': {
      opacity: 0.6,
    },
  };
});

ServiceToggleButton.propTypes = {
  serviceType: PropTypes.oneOf([SERVICE_TYPES.LUNCH, SERVICE_TYPES.DINNER]).isRequired,
  children: PropTypes.node,
};

export default ServiceToggleButton;
